import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, List, ListItem, ListItemIcon, ListItemText, Stack, TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { providerApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Seeded-profile confirmation (spec: owner confirmation). Renders only for a
 * claimed seeded provider that hasn't confirmed yet. The readiness checklist is
 * rendered verbatim from the backend — one validation source, no drift.
 */
const ConfirmationBanner = () => {
  const { providerData, refreshProviderData } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [keepTypes, setKeepTypes] = useState([]);
  const [publicPhone, setPublicPhone] = useState('');
  const [publicWebsite, setPublicWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const shouldShow = Boolean(providerData?.seeded && providerData?.claimed && !providerData?.ownerConfirmed);

  useEffect(() => {
    if (!shouldShow || !open) return;
    providerApi.getReadiness().then(setReadiness).catch(() => setReadiness(null));
    setKeepTypes(providerData?.declaredServiceTypes || []);
    setPublicPhone(providerData?.publicPhone || '');
    setPublicWebsite(providerData?.publicWebsite || '');
    setError(null);
  }, [shouldShow, open, providerData]);

  if (!shouldShow) return null;

  const toggleType = (type) => {
    setKeepTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await providerApi.confirmProfile({
        keepDeclaredTypes: keepTypes,
        publicContactReviewed: true,
        publicPhone: publicPhone.trim() || null,
        publicWebsite: publicWebsite.trim() || null,
      });
      await refreshProviderData();
      setOpen(false);
    } catch (e) {
      setError(e.response?.data?.message || e.message || t('confirm.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const serviceReady = readiness?.items?.find((i) => i.key === 'pricedService')?.satisfied ?? false;

  return (
    <>
      <Alert
        severity="info"
        sx={{ mb: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => setOpen(true)}>
            {t('confirm.reviewButton')}
          </Button>
        }
      >
        {t('confirm.bannerBody')}
      </Alert>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('confirm.dialogTitle')}</DialogTitle>
        <DialogContent>
          {!readiness ? (
            <CircularProgress size={24} />
          ) : (
            <Stack spacing={2}>
              <List dense>
                {readiness.items.map((item) => (
                  <ListItem key={item.key} disableGutters>
                    <ListItemIcon>
                      {item.satisfied ? (
                        <CheckCircleOutlineIcon color="success" />
                      ) : (
                        <RadioButtonUncheckedIcon color="disabled" />
                      )}
                    </ListItemIcon>
                    <ListItemText primary={item.detail || item.key} />
                  </ListItem>
                ))}
              </List>

              {(providerData?.declaredServiceTypes || []).length > 0 && (
                <>
                  <Typography variant="subtitle2">
                    {t('confirm.keepTypesTitle')}
                  </Typography>
                  {(providerData?.declaredServiceTypes || []).map((type) => (
                    <FormControlLabel
                      key={type}
                      control={
                        <Checkbox checked={keepTypes.includes(type)} onChange={() => toggleType(type)} />
                      }
                      label={type}
                    />
                  ))}
                </>
              )}

              <Typography variant="subtitle2">
                {t('confirm.publicContactTitle')}
              </Typography>
              <TextField
                label={t('confirm.publicPhone')}
                value={publicPhone}
                onChange={(e) => setPublicPhone(e.target.value)}
                size="small"
              />
              <TextField
                label={t('confirm.publicWebsite')}
                value={publicWebsite}
                onChange={(e) => setPublicWebsite(e.target.value)}
                size="small"
              />

              {!serviceReady && (
                <Alert severity="warning">
                  {t('confirm.needPricedService')}
                </Alert>
              )}
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('confirm.later')}</Button>
          <Button variant="contained" onClick={handleConfirm} disabled={submitting || !serviceReady}>
            {submitting ? t('confirm.confirming') : t('confirm.confirmButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConfirmationBanner;
